import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import { sort } from 'ramda';
import React from 'react';
import styled, { css } from 'styled-components';

import { FlowTemplates } from '~/graphql/types';
import { FlowTemplatesCategory } from '../../index';
import TEST_ID from './index.testid';
import SelectBlock from '~/components/molecule/SelectBlock';
import { scrollBarStyles } from '~/components/molecule/OverflowScrollWrapper';
import useTrackEvent from '~/hooks/useTrackEvent';

type Props = RouteComponentProps<{ slug: string }> & {
  flowCategories: FlowTemplatesCategory;
  onSelect: (selectedTemplate: FlowTemplates | null) => void;
  templateId?: string | null;
};

const TemplateDetails: React.FCC<Props> = ({
  slug,
  flowCategories,
  templateId,
  onSelect,
}) => {
  const trackEvent = useTrackEvent();
  const currentCategory: FlowTemplatesCategory['key'] = !slug
    ? flowCategories['meest-gebruikt']
    : flowCategories[slug];

  const sortedTemplates = currentCategory?.templates
    ? sort(({ name: a, weight: wA = 0 }, { name: b, weight: wB = 0 }) => {
        if (wA === wB) return a.localeCompare(b);
        return wB - wA;
      }, currentCategory.templates)
    : [];

  const handleSelect = (template: FlowTemplates) => {
    /** ----- TRACKING: flow_template_selected ----- */
    trackEvent({
      eventName: 'flow_selected_template',
      parameters: {
        template_id: template.id,
        template_name: template.name,
      },
    });
    onSelect(template);
  };

  return (
    <Container>
      {sortedTemplates.map(template => (
        <SelectBlock
          type="radio"
          key={template.id}
          checked={templateId === template.id}
          onClick={() => handleSelect(template)}
          description={template.description}
          checkboxTitle={template.name}
          dataTestId={TEST_ID.TEMPLATE}
        />
      ))}
    </Container>
  );
};

const Container = styled.div(
  ({ theme }) => css`
    padding: ${theme.space('s')} ${theme.space('l')};

    /* Magic number 63 to show 4 full entries (most of the time) */
    height: 63vh;
    overflow-y: auto;
    width: 100%;

    ${scrollBarStyles}
  `,
);

export default TemplateDetails;
